import { i18nTranslationHelper } from '@/i18n'
import { FormHelperText, InputBaseComponentProps, TextField, TextFieldProps } from '@mui/material'
import PhoneInput, { Country } from 'react-phone-number-input'
import { PhoneNumberUtil } from 'google-libphonenumber'
import 'react-phone-number-input/style.css'
import { ReactNode, Fragment, Ref, useEffect, useState } from 'react'
import {
  Controller,
  FieldError,
  FieldValues,
  UseControllerProps,
  useFormContext
} from 'react-hook-form'
import FieldLabelTooltip from '@/presentation/components/ui/input/field-label-tooltip'
import { InputValidationEndAdornment } from '../input-validation-end-adornment'

export type PhoneFieldProps<T extends FieldValues> = TextFieldProps &
  UseControllerProps<T> & {
    label?: string | ReactNode
    placeholder?: string
    tooltipMessage?: string
    isVerified?: boolean
    showEndAdornment?: boolean
    helperText?: string
  }

const DEFAULT_COUNTRY: Country = 'BR'
const phoneUtil = PhoneNumberUtil.getInstance()

const importLabels = async (locale: string): Promise<Record<string, string>> => {
  switch (locale) {
    case 'es': {
      const m = await import('react-phone-number-input/locale/es.json')
      return m.default as Record<string, string>
    }
    case 'en': {
      const m = await import('react-phone-number-input/locale/en.json')
      return m.default as Record<string, string>
    }
    case 'pt':
    default: {
      const m = await import('react-phone-number-input/locale/pt.json')
      return m.default as Record<string, string>
    }
  }
}

interface PhoneLibAdapterProps extends InputBaseComponentProps {
  labels?: Record<string, string>
}

function PhoneLibAdapter({
  onChange,
  value,
  disabled,
  labels,
  className,
  style,
  ref,
  ...rest
}: PhoneLibAdapterProps & { ref?: Ref<HTMLInputElement> }) {
  return (
    <PhoneInput
      ref={ref as never}
      value={(value as string) || undefined}
      onChange={val =>
        onChange?.({ target: { value: val ?? '' } } as React.ChangeEvent<HTMLInputElement>)
      }
      disabled={disabled}
      defaultCountry={DEFAULT_COUNTRY}
      countryOptionsOrder={[DEFAULT_COUNTRY, '|', '...']}
      addInternationalOption={false}
      labels={labels}
      className={className}
      style={{ display: 'flex', alignItems: 'center', width: '100%', ...style }}
      numberInputProps={rest}
    />
  )
}

const parseHelperText = (error: FieldError | undefined, defaultErrorMessage: string) =>
  error?.type == 'validate' ? error?.message ?? defaultErrorMessage : error?.message

export default function PhoneField<T extends FieldValues>({
  name,
  control,
  required,
  disabled,
  fullWidth = true,
  label = 'Celular',
  tooltipMessage,
  variant = 'outlined',
  size = 'small',
  slotProps: callerSlotProps,
  placeholder = '',
  rules,
  className,
  isVerified = false,
  showEndAdornment = false,
  helperText = '',
  ...props
}: PhoneFieldProps<T>) {
  const t = {
    validations: i18nTranslationHelper.getTValidations,
    fields: i18nTranslationHelper.getTEntitiesFields
  }

  const formContext = useFormContext<T>()
  const mergedControl = formContext?.control ?? control

  const [labels, setLabels] = useState<Record<string, string>>({})
  const [language] = useState<string>(i18nTranslationHelper.getCurrentLanguage())

  useEffect(() => {
    importLabels(language).then(setLabels)
  }, [language])

  const errorMessages = {
    phoneNumberInvalidError: t.validations('invalidPhoneNumberError'),
    phoneNumberDoNotExistError: t.validations('invalidPhoneNumberDoNotExist')
  }

  const resolvedRules = {
    ...rules,
    required: required ? t.validations('required') : false,
    validate: (value?: string) => {
      if (!required && !value) return true
      if (!value) return errorMessages.phoneNumberInvalidError
      try {
        return (
          phoneUtil.isValidNumber(phoneUtil.parse(value)) || errorMessages.phoneNumberInvalidError
        )
      } catch {
        return errorMessages.phoneNumberDoNotExistError
      }
    }
  }

  const id = `${label}-phone`

  const finalLabel = tooltipMessage ? (
    <FieldLabelTooltip label={label} toolTipTitle={tooltipMessage} />
  ) : (
    <Fragment>{label}</Fragment>
  )

  const loweredCaseLabel = typeof label === 'string' ? label.toLowerCase() : ''
  const defaultErrorMessage = t.validations('invalidPhoneNumberError')

  return (
    <div className={`text-start w-full ${className}`}>
      <Controller
        name={name}
        control={mergedControl}
        rules={resolvedRules}
        render={({ field, fieldState }) => (
          <Fragment>
            <TextField
              {...props}
              {...field}
              id={id}
              label={finalLabel}
              disabled={disabled}
              placeholder={placeholder}
              fullWidth={fullWidth}
              variant={variant}
              size={size}
              value={field.value || ''}
              sx={{
                ...(disabled ? { pointerEvents: 'none' } : {}),
                '& .MuiInputBase-input': { display: 'flex', alignItems: 'center', height: 'auto' },
                '& .PhoneInputInput': {
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  font: 'inherit',
                  color: 'inherit',
                  background: 'transparent',
                  minWidth: 0,
                  p: 0
                }
              }}
              error={!!fieldState.error}
              helperText={parseHelperText(fieldState.error, defaultErrorMessage)}
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  endAdornment: showEndAdornment && (
                    <InputValidationEndAdornment
                      isVerified={isVerified}
                      title={isVerified ? '' : t.fields('phone.notVerifiedTooltip')}
                    />
                  ),
                  'aria-placeholder': t.fields('phone.ariaPlaceholder', {
                    label: loweredCaseLabel,
                    placeholder
                  }),
                  ...callerSlotProps?.input,
                  inputComponent: PhoneLibAdapter
                },
                htmlInput: {
                  'data-testid': `text-field-${name}`,
                  labels,
                  ...callerSlotProps?.htmlInput
                } as unknown as React.InputHTMLAttributes<HTMLInputElement> & {
                  labels?: Record<string, string>
                }
              }}
            />

            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </Fragment>
        )}
      />
    </div>
  )
}
